import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { userAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Watermark } from './Watermark';
import { useCaptureProtection } from '../../hooks/useCaptureProtection';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { AlertTriangle } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import {
  LayoutDashboard,
  Package,
  Tags,
  Target,
  BarChart3,
  Bot,
  Users,
  Shield,
  Settings,
  ClipboardList,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Bell,
  Database,
  TrendingUp,
  FileSpreadsheet,
  Brain,
  Search,
  Trophy,
  ShieldAlert,
  Link2,
  ReceiptText,
  GraduationCap,
  ShieldCheck,
  CalendarCheck2,
  Radar,
  KeyRound,
  Boxes,
  UserPlus,
} from 'lucide-react';
import api from '../../lib/api';
import NotificationBell from './NotificationBell';
import VendorReviewBell from './VendorReviewBell';
import HierarchyDriftBell from './HierarchyDriftBell';
import PreviewSyncButton from './PreviewSyncButton';
import ZohoSyncPill from './ZohoSyncPill';
import { ChangePasswordDialog } from './ChangePasswordDialog';

const navigation = [
  { name: 'Today Dashboard', href: '/', icon: LayoutDashboard, module: 'DASHBOARD' },
  { name: 'Categories', href: '/categories', icon: Tags, module: 'CATEGORIES' },
  { name: 'Items', href: '/items', icon: Package, module: 'ITEMS' },
  { name: 'Risk Dashboard', href: '/risk-dashboard', icon: ShieldAlert, module: 'RECONCILE' },
  { name: 'Targets', href: '/targets', icon: Target, module: 'TARGETS' },
  { name: 'Bid Engine', href: '/bid-engine', icon: TrendingUp, module: 'BID_ENGINE' },
  { name: 'Offer Store', href: '/offer-store', icon: Boxes, module: 'OFFER_STORE' },
  { name: 'Vendor Payment Approval', href: '/vendor-payment', icon: ReceiptText, module: 'VENDOR_PAYMENT_APPROVAL' },
  { name: 'Performance Reviews', href: '/reviews', icon: GraduationCap, module: 'PERFORMANCE_REVIEWS' },
  { name: 'Reports', href: '/reports', icon: BarChart3, module: 'REPORTS' },
  { name: 'Performance', href: '/performance', icon: TrendingUp, module: 'PERFORMANCE' },
  { name: 'My Team', href: '/my-team', icon: Users, module: 'DASHBOARD', managersOnly: true },
  { name: 'Zoho Inventory', href: '/zoho-inventory', icon: Database, module: 'ZOHO_SYNC' },
  { name: 'Vendors & Customers', href: '/directory', icon: Users, module: 'DIRECTORY' },
  { name: 'Bulk Import/Export', href: '/bulk-import-export', icon: FileSpreadsheet, module: 'IMPORT_EXPORT' },
  { name: 'AI Assistant', href: '/ai-assistant', icon: Bot, module: 'AI_ASSISTANT' },
  { name: 'AI Trade Engine', href: '/ai-engine', icon: Brain, module: 'AI_ENGINE' },
  { name: 'Performance (PMS)', href: '/pms', icon: Trophy, module: 'PMS' },
  { name: 'Conduct', href: '/conduct', icon: ShieldCheck, module: 'CONDUCT', everyone: true },
  { name: 'Visits & Meetings', href: '/meetings', icon: CalendarCheck2, module: 'MEETINGS' },
  { name: 'Sourcing', href: '/sourcing', icon: Radar, module: 'SOURCING' },
  { name: 'Leads', href: '/leads', icon: UserPlus, module: 'LEADS' },
];

const adminNavigation = [
  { name: 'AI Metrics', href: '/metrics', icon: BarChart3, module: 'AI_METRICS' },
  { name: 'Users', href: '/users', icon: Users, module: 'USERS' },
  { name: 'Roles & Profiles', href: '/roles', icon: Shield, module: 'ROLES_PROFILES' },
  { name: 'Settings', href: '/settings', icon: Settings, module: 'SETTINGS' },
  { name: 'Audit Log', href: '/audit-log', icon: ClipboardList, module: 'AUDIT_LOG' },
  { name: 'Reconcile', href: '/reconcile', icon: Shield, module: 'RECONCILE' },
  { name: 'CRM Integration', href: '/crm-matching', icon: Link2, module: 'USERS' },
  { name: 'Alias Catalog', href: '/alias-catalog', icon: Database, module: 'SETTINGS' },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const { user, logout, canAccess, isAdmin: isAdminUser, updateUser } = useAuth();
  useCaptureProtection(user);
  const location = useLocation();
  const navigate = useNavigate();

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults(null); return; }
    try {
      const res = await api.get(`/search/global?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data);
      setSearchOpen(true);
    } catch (e) { setSearchResults(null); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, doSearch]);

  useEffect(() => {
    const handleClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const [vpPendingCount, setVpPendingCount] = useState(0);
  useEffect(() => {
    if (!canAccess('VENDOR_PAYMENT_APPROVAL', 'APPROVE')) return;
    const fetchCount = async () => {
      try {
        const res = await api.get('/vendor-payment/pending-approval-count');
        setVpPendingCount(res.data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    window.addEventListener('focus', fetchCount);
    return () => { clearInterval(interval); window.removeEventListener('focus', fetchCount); };
  }, [canAccess]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const filteredNavigation = navigation.filter(
    (item) => (item.everyone || canAccess(item.module, 'VIEW'))
      && (item.name !== 'Reports' || isAdminUser)
      && (!item.managersOnly || user?.has_reports || user?.data_scope === 'ALL')
  );

  const filteredAdminNavigation = adminNavigation.filter(
    (item) => canAccess(item.module, 'VIEW')
  );

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleResetPassword = async () => {
    if (newPassword.length < 10) { toast.error('Password must be at least 10 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setResetting(true);
    try {
      await userAPI.changePassword({ new_password: newPassword });
      toast.success('Password changed successfully');
      updateUser({ must_reset_password: false });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
    finally { setResetting(false); }
  };

  const isPreviewEnv = user?.app_env === 'preview';

  // Force a password change before anything else can be reached — no sidebar, no outlet.
  if (user?.must_reset_password) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="force-password-reset-screen">
        <div className="w-full max-w-sm space-y-4 border border-border rounded-sm p-6 bg-card">
          <div>
            <h2 className="text-lg font-semibold">Set Your Password</h2>
            <p className="text-sm text-muted-foreground mt-1">Please set a new password to continue. You can't access TradeIQ until this is done.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label>New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 10 characters" data-testid="new-password" />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" data-testid="confirm-password" />
            </div>
          </div>
          <Button onClick={handleResetPassword} disabled={resetting || !newPassword} className="rounded-sm w-full" data-testid="set-password-btn">
            {resetting ? 'Setting...' : 'Set Password'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen bg-background overflow-x-clip', isPreviewEnv && 'pt-7')}>
      <Watermark />
      <p className="print-disabled-message">Printing Trade IQ is disabled. This attempt has been logged.</p>
      {/* Preview environment banner */}
      {isPreviewEnv && (
        <div
          className="fixed top-0 inset-x-0 z-[60] h-7 flex items-center justify-center gap-1.5 bg-amber-400 text-amber-950 text-xs font-medium"
          data-testid="preview-env-banner"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          PREVIEW — test environment. Changes are not sent to Zoho.
        </div>
      )}
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed bottom-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:translate-x-0',
          isPreviewEnv ? 'top-7' : 'top-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            <Link to="/" className="flex items-center gap-2">
              <img src="/vstl_logo_small.png" alt="VSTL" className="h-10 w-auto" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors',
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                    {item.name}
                    {item.module === 'VENDOR_PAYMENT_APPROVAL' && vpPendingCount > 0 && (
                      <span
                        className="ml-auto flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold"
                        data-testid="vp-pending-approval-badge"
                      >
                        {vpPendingCount > 99 ? '99+' : vpPendingCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {filteredAdminNavigation.length > 0 && (
              <>
                <div className="px-3 py-4">
                  <div className="border-t border-border" />
                </div>
                <div className="px-3 mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Admin
                  </span>
                </div>
                <nav className="px-3 space-y-1">
                  {filteredAdminNavigation.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors',
                          active
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>
              </>
            )}
          </ScrollArea>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className={cn('sticky z-30 h-16 bg-card/95 backdrop-blur border-b border-border', isPreviewEnv ? 'top-7' : 'top-0')}>          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h1 className="text-lg font-semibold hidden sm:block">
                {filteredNavigation.find((n) => isActive(n.href))?.name ||
                  filteredAdminNavigation.find((n) => isActive(n.href))?.name ||
                  'Dashboard'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Global Search */}
              {canAccess('ITEMS', 'VIEW') && (
              <div className="relative hidden sm:block" ref={searchRef}>
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setSearchOpen(true)}
                  placeholder="Search items, vendors, customers..."
                  className="w-64 pl-8 h-9 text-sm rounded-sm"
                  data-testid="global-search-input"
                />
                {searchOpen && searchResults && (
                  <div className="absolute top-full mt-1 w-80 bg-card border rounded-sm shadow-lg z-50 max-h-80 overflow-y-auto">
                    {searchResults.items?.length > 0 && (
                      <div>
                        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">Items</div>
                        {searchResults.items.map((item, i) => (
                          <div key={i} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm flex justify-between"
                            onClick={() => { navigate('/items'); setSearchOpen(false); setSearchQuery(''); }}>
                            <div><span className="font-mono font-medium">{item.sku}</span><span className="text-muted-foreground ml-2 text-xs">{item.name}</span></div>
                            <span className="text-xs text-muted-foreground">Stock: {item.stock}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {searchResults.vendors?.length > 0 && (
                      <div>
                        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">Vendors</div>
                        {searchResults.vendors.map((v, i) => (
                          <div key={i} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                            onClick={() => { navigate('/zoho-inventory'); setSearchOpen(false); setSearchQuery(''); }}>
                            <span className="font-medium">{v.name}</span>
                            {v.company && <span className="text-muted-foreground ml-2 text-xs">{v.company}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {searchResults.customers?.length > 0 && (
                      <div>
                        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">Customers</div>
                        {searchResults.customers.map((c, i) => (
                          <div key={i} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm flex justify-between"
                            onClick={() => { navigate('/zoho-inventory'); setSearchOpen(false); setSearchQuery(''); }}>
                            <span className="font-medium">{c.name}</span>
                            <span className="text-xs text-muted-foreground">{c.orders} orders</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {!searchResults.items?.length && !searchResults.vendors?.length && !searchResults.customers?.length && (
                      <div className="px-3 py-4 text-center text-sm text-muted-foreground">No results found</div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Vendor review (admin only) */}
              {isAdminUser && <VendorReviewBell />}
              {isAdminUser && <HierarchyDriftBell />}

              {/* Preview-only manual Zoho sync (admin only) — hidden on phones; not core Sourcing functionality */}
              {isPreviewEnv && isAdminUser && (
                <div className="hidden sm:block"><PreviewSyncButton /></div>
              )}

              {/* Zoho sync status pill — all users, hidden on phones to avoid header overflow */}
              <div className="hidden sm:block"><ZohoSyncPill /></div>

              {/* Notifications */}
              <NotificationBell />

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium max-w-[120px] truncate">
                      {user?.name}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-xs text-muted-foreground">
                    {user?.profile_name || user?.roles?.join(', ') || 'None'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setChangePasswordOpen(true)} data-testid="change-password-menu-item">
                    <KeyRound className="h-4 w-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-destructive focus:text-destructive"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </div>
  );
}
